import { useState, useEffect, useRef } from 'react';
import styles from './UnifiedAuthModal.module.scss';
import EZIOKService, { EZIOKResult } from '../../libs/EZIOKService';
import MobileOKService from '../../libs/MobileOKService';
import axiosClient from '../../libs/axiosClient';
import kakaoLogo from '../../assets/authLogoImages/100_kakaotalk.png';
import naverLogo from '../../assets/authLogoImages/100_naver.png';
import passLogo from '../../assets/authLogoImages/100_pass.png';
import tossLogo from '../../assets/authLogoImages/100_toss.png';
import shinhanLogo from '../../assets/authLogoImages/100_ShinhanSIGN.png';
import kbstarLogo from '../../assets/authLogoImages/100_KBstar.png';
import hanaLogo from '../../assets/authLogoImages/100_hana.png';
import wooriLogo from '../../assets/authLogoImages/100_WooRi_WON.png';
import nhLogo from '../../assets/authLogoImages/100_NH_NEW.png';
import kakaobankLogo from '../../assets/authLogoImages/100_kakaobank.png';
import banksaladLogo from '../../assets/authLogoImages/100_banksalad.png';
import samsungpassLogo from '../../assets/authLogoImages/100_SamsungPass.png';
import yeskeyLogo from '../../assets/authLogoImages/logo_yeskey_1.png';

// 성공 코드 상수
const SUCCESS_CODES = ['0', '0000', '2000'];

interface AuthProvider {
  code: string;
  name: string;
  type: string;
  logo: string | null;
  ciStorable: boolean;
  authMethod: 'easy' | 'mobile';
}

// 전체 인증 제공사 목록 (간편인증 + 휴대폰인증)
const AUTH_PROVIDERS: AuthProvider[] = [
  { code: 'kakao', name: '카카오', type: '민간', logo: kakaoLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'naver', name: '네이버', type: '민간', logo: naverLogo, ciStorable: true, authMethod: 'easy' }, // 임시 비활성화
  { code: 'passauth', name: 'PASS', type: '통신사', logo: passLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'toss', name: '토스', type: '금융', logo: tossLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'shinhan', name: '신한은행', type: '금융', logo: shinhanLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'kbstar', name: 'KB국민은행', type: '금융', logo: kbstarLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'kebhana', name: '하나은행', type: '금융', logo: hanaLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'woori', name: '우리은행', type: '금융', logo: wooriLogo, ciStorable: false, authMethod: 'easy' },
  { code: 'nhlife', name: 'NH농협', type: '금융', logo: nhLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'kakaobank', name: '카카오뱅크', type: '금융', logo: kakaobankLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'banksalad', name: '뱅크샐러드', type: '금융', logo: banksaladLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'signgate', name: '삼성패스', type: '공동인증', logo: samsungpassLogo, ciStorable: false, authMethod: 'easy' },
  { code: 'yeskey', name: '금융결제원', type: '공동인증', logo: yeskeyLogo, ciStorable: false, authMethod: 'easy' },
  // 휴대폰 인증 (마지막에 배치)
  { code: 'mobile', name: '휴대폰인증', type: 'SMS', logo: null, ciStorable: true, authMethod: 'mobile' },
];

interface UnifiedAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: EZIOKResult) => void;
  onError?: (error: Error) => void;
  userId?: number;
  forRegistration?: boolean;
  // 비즈니스 파라미터 (주문/결제 관련)
  pcode?: number;           // 상품 ID
  cartString?: string;      // 장바구니 문자열
  isDirect?: string;        // 직접구매 여부 (Y/N)
  purpose?: 'order' | 'signup' | 'adult' | 'general';  // 인증 용도
  // redirect 방식 파라미터 (인앱 브라우저용)
  pendingData?: any;        // redirect 후 복원할 데이터
  returnPath?: string;      // redirect 후 돌아갈 경로
}

const UnifiedAuthModal: React.FC<UnifiedAuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  userId,
  forRegistration = false,
  purpose = 'general',
  // pendingData, returnPath는 인터페이스에만 유지 (backward compatibility)
  // 서비스가 내부적으로 auth_return_path를 sessionStorage에 저장
}) => {
  const [selectedProvider, setSelectedProvider] = useState<AuthProvider | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSdkReady, setIsSdkReady] = useState(false);

  // 저장된 인증 결과 확인 (인앱 브라우저에서 redirect 후 돌아왔을 때)
  useEffect(() => {
    const storedResult = sessionStorage.getItem('eziok_authResult');
    if (storedResult) {
      try {
        const authResult = JSON.parse(storedResult);
        sessionStorage.removeItem('eziok_authResult');
        console.log('[UnifiedAuthModal] Found stored auth result:', authResult);

        if (authResult.success) {
          onSuccess({
            success: true,
            message: authResult.message || '인증 성공',
            data: authResult.data || {},
          });
        } else {
          onError?.(new Error(authResult.message || '간편인증에 실패했습니다.'));
        }
      } catch (err) {
        console.error('[UnifiedAuthModal] Failed to parse stored auth result:', err);
        sessionStorage.removeItem('eziok_authResult');
      }
    }
  }, [onSuccess, onError]);

  // 모달 열릴 때 SDK 미리 로드
  useEffect(() => {
    if (isOpen) {
      const loadSDKs = async () => {
        try {
          await Promise.all([
            MobileOKService.loadScript(),
            EZIOKService.loadScript(),
          ]);
          setIsSdkReady(true);
          console.log('[UnifiedAuthModal] SDKs loaded successfully');
        } catch (err) {
          console.error('[UnifiedAuthModal] SDK load failed:', err);
        }
      };
      loadSDKs();
    }
  }, [isOpen]);

  // postMessage 이벤트 리스너 (백엔드 /callback에서 HTML 응답으로 postMessage 전송)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('[UnifiedAuthModal] postMessage received:', event.data, 'from:', event.origin);

      const data = event.data;
      if (typeof data !== 'object' || data === null) {
        return;
      }

      // redirect 결과 처리 (팝업에서 postMessage로 전달된 경우)
      if (data.type === 'EZIOK_REDIRECT_RESULT') {
        console.log('[UnifiedAuthModal] EZIOK redirect result:', data);

        if (data.success) {
          setIsLoading(false);
          onSuccess({
            success: true,
            message: '인증 성공',
            data: data.data || {},
          });
        } else {
          setIsLoading(false);
          onError?.(new Error(data.message || '간편인증에 실패했습니다.'));
        }
        return;
      }

      // EZ-IOK/Mobile-OK 콜백 페이지에서 postMessage로 전달한 결과
      // success가 boolean (true/false) 또는 문자열 ('true'/'false') 둘 다 처리
      if (data.success === true || data.success === 'true') {
        console.log('[UnifiedAuthModal] 본인인증 성공:', data);
        setIsLoading(false);
        onSuccess({
          success: true,
          message: data.message || '인증 성공',
          data: data.data || {},
        });
        // onClose는 호출하지 않음 - handleAuthSuccess에서 모달을 닫음
      } else if (data.success === false || data.success === 'false') {
        console.log('[UnifiedAuthModal] 본인인증 실패:', data.message);
        setIsLoading(false);
        onError?.(new Error(data.message || '본인인증에 실패했습니다.'));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSuccess, onError, onClose]);

  // props를 ref로 유지 (콜백에서 최신값 참조)
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const userIdRef = useRef(userId);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    userIdRef.current = userId;
  }, [onSuccess, onError, userId]);

  // SDK 콜백 등록
  useEffect(() => {
    // Mobile-OK 콜백 - mokToken으로 /result API 호출
    (window as any).mobileOKCallback = async (response: string) => {
      console.log('[UnifiedAuthModal] mobileOKCallback called:', response);

      try {
        const data = JSON.parse(response);
        console.log('[UnifiedAuthModal] Parsed MobileOK data:', data);

        if (SUCCESS_CODES.includes(String(data.resultCode))) {
          const mokToken = data.encryptMOKKeyToken || data.mokToken;
          const storedTxId = sessionStorage.getItem('mobileok_txId');

          if (mokToken && storedTxId) {
            console.log('[UnifiedAuthModal] Calling /result API with mokToken');
            try {
              const resultResponse = await axiosClient.post('/func/mobileok/result', {
                mokToken,
                txId: storedTxId,
                userId: userIdRef.current,
              });

              sessionStorage.removeItem('mobileok_txId');
              const resultData = resultResponse.data;

              if (SUCCESS_CODES.includes(String(resultData.resultCode))) {
                setIsLoading(false);
                onSuccessRef.current({
                  success: true,
                  message: resultData.resultMsg || '인증 성공',
                  data: {
                    name: resultData.name,
                    ci: resultData.ci,
                    di: resultData.di,
                    birthDate: resultData.birthDate,
                    gender: resultData.gender,
                    mobileNo: resultData.mobileNo,
                    nationalInfo: resultData.nationalInfo,
                  },
                });
              } else {
                setIsLoading(false);
                onErrorRef.current?.(new Error(resultData.resultMsg || '본인확인 결과 조회에 실패했습니다.'));
              }
            } catch (apiErr) {
              console.error('[UnifiedAuthModal] MobileOK result API error:', apiErr);
              setIsLoading(false);
              // API 실패 시 기존 데이터로 성공 처리
              onSuccessRef.current({
                success: true,
                message: data.resultMsg || '인증 성공',
                data: data.data || data,
              });
            }
          } else {
            // mokToken 없으면 기존 방식
            setIsLoading(false);
            onSuccessRef.current({
              success: true,
              message: data.resultMsg || '인증 성공',
              data: data.data || data,
            });
          }
        } else {
          setIsLoading(false);
          onErrorRef.current?.(new Error(data.resultMsg || '본인인증에 실패했습니다.'));
        }
      } catch (err) {
        console.error('[UnifiedAuthModal] mobileOKCallback parse error:', err);
        setIsLoading(false);
        onErrorRef.current?.(new Error('본인인증 처리 중 오류가 발생했습니다.'));
      }
    };

    // EZ-IOK 콜백 - keyToken으로 /result API 호출
    (window as any).eziokCallback = async (response: string) => {
      console.log('[UnifiedAuthModal] eziokCallback called:', response);

      try {
        // JSON 형식 처리 (백엔드에서 직접 결과 반환 시)
        if (response.startsWith('{')) {
          const data = JSON.parse(response);
          console.log('[UnifiedAuthModal] Parsed EZIOK JSON data:', data);

          if (SUCCESS_CODES.includes(String(data.resultCode))) {
            setIsLoading(false);
            onSuccessRef.current({
              success: true,
              message: data.resultMsg || '인증 성공',
              data: {
                name: data.name,
                ci: data.ci,
                di: data.di,
                birthDate: data.birthDate,
                gender: data.gender,
                mobileNo: data.mobileNo,
              },
            });
          } else {
            setIsLoading(false);
            onErrorRef.current?.(new Error(data.resultMsg || '간편인증에 실패했습니다.'));
          }
          return;
        }

        // 파이프 구분자 형식 처리 (SDK 형식: resultCode|keyToken|errorMsg)
        const parts = response.split('|');
        const resultCode = parts[0];
        const keyToken = parts[1] || '';
        const errorMsg = parts[2] || '';

        console.log('[UnifiedAuthModal] Parsed EZIOK pipe format:', { resultCode, keyToken, errorMsg });

        if (SUCCESS_CODES.includes(String(resultCode))) {
          const storedTxId = sessionStorage.getItem('eziok_txId');

          if (keyToken && storedTxId) {
            console.log('[UnifiedAuthModal] Calling /result API with keyToken');
            try {
              const resultResponse = await axiosClient.post('/func/eziok/result', {
                keyToken,
                txId: storedTxId,
                userId: userIdRef.current,
              });

              sessionStorage.removeItem('eziok_txId');
              const resultData = resultResponse.data;

              if (SUCCESS_CODES.includes(String(resultData.resultCode))) {
                setIsLoading(false);
                onSuccessRef.current({
                  success: true,
                  message: resultData.resultMsg || '인증 성공',
                  data: {
                    name: resultData.name,
                    ci: resultData.ci,
                    di: resultData.di,
                    birthDate: resultData.birthDate,
                    gender: resultData.gender,
                    mobileNo: resultData.mobileNo,
                  },
                });
              } else {
                setIsLoading(false);
                onErrorRef.current?.(new Error(resultData.resultMsg || '간편인증 결과 조회에 실패했습니다.'));
              }
            } catch (apiErr) {
              console.error('[UnifiedAuthModal] EZIOK result API error:', apiErr);
              setIsLoading(false);
              onErrorRef.current?.(new Error('간편인증 결과 조회에 실패했습니다.'));
            }
          } else if (!keyToken) {
            // keyToken 없으면 중간 단계 - 대기
            console.log('[UnifiedAuthModal] EZIOK auth started (waiting for callback)');
          }
        } else {
          setIsLoading(false);
          onErrorRef.current?.(new Error(errorMsg || '간편인증에 실패했습니다.'));
        }
      } catch (err) {
        console.error('[UnifiedAuthModal] eziokCallback parse error:', err);
        setIsLoading(false);
        onErrorRef.current?.(new Error('간편인증 처리 중 오류가 발생했습니다.'));
      }
    };

    return () => {
      delete (window as any).mobileOKCallback;
      delete (window as any).eziokCallback;
    };
  }, []);

  // 모달 열릴 때 선택 초기화
  useEffect(() => {
    if (isOpen) {
      setSelectedProvider(null);
    }
  }, [isOpen]);

  // 회원가입용이면 CI 저장 가능한 제공사만 필터링
  const availableProviders = forRegistration
    ? AUTH_PROVIDERS.filter((p) => p.ciStorable)
    : AUTH_PROVIDERS;

  // 인증하기 버튼 클릭
  const handleAuth = async () => {
    if (!selectedProvider) return;

    setIsLoading(true);

    try {
      if (selectedProvider.authMethod === 'mobile') {
        // 휴대폰 인증 (Mobile-OK) - MOKToken 방식
        // 서비스가 내부적으로 환경 감지하여 callback/redirect 방식 자동 선택
        await MobileOKService.startAuth({
          usageCode: '01005',
          userId,
        });
        // 팝업이 열리면 로딩 해제 (일반 브라우저)
        // 인앱 브라우저에서는 redirect되어 여기 실행 안됨
        setIsLoading(false);
      } else {
        // 간편인증 (EZ-IOK) - keytoken 방식
        // 서비스가 내부적으로 환경 감지하여 callback/redirect 방식 자동 선택
        await EZIOKService.startAuth({
          userId,
          authProviderCode: selectedProvider.code,
        });

        // 일반 브라우저: 팝업이 열리면 로딩 해제
        // 인앱 브라우저에서는 SDK가 자동으로 redirect하므로 여기 실행 안됨
        setIsLoading(false);
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('인증 요청 실패'));
      setIsLoading(false);
    }
  };

  // 인증하기 버튼 활성화 조건
  const isAuthButtonEnabled = () => {
    if (isLoading || !selectedProvider || !isSdkReady) return false;
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className={styles.header}>
          <h2 className={styles.title}>본인인증</h2>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        {/* 콘텐츠 */}
        <div className={styles.content}>
          {/* 진료용 안내 가이드 */}
          {purpose === 'order' && (
            <div className={styles.guideSection}>
              <p className={styles.guideText}>
                비대면진료 시범사업 개정('24.12.2.)으로 본인 인증이 필요합니다.
              </p>
              <p className={styles.guideText}>
                인증은 <span>6개월</span>에 한번 진행되며, 본인 명의로 인증해 주세요.
              </p>
              <p className={styles.warningText}>
                간편인증에 어려움이 있으신 경우 휴대폰인증을 이용하시거나
              </p>
              <p className={styles.warningText}>
                하단 물음표 버튼으로 고객센터에 문의해 주세요.
              </p>
            </div>
          )}
          <div className={styles.providerGrid}>
            {availableProviders.map((provider) => (
              <button
                key={provider.code}
                className={`${styles.providerItem} ${
                  selectedProvider?.code === provider.code ? styles.selected : ''
                }`}
                onClick={() => setSelectedProvider(provider)}
              >
                {provider.logo ? (
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    className={styles.providerLogo}
                  />
                ) : (
                  <div className={styles.mobileIconSmall}>📱</div>
                )}
                <span className={styles.providerName}>{provider.name}</span>
              </button>
            ))}
          </div>
          {forRegistration && (
            <p className={styles.notice}>
              * 회원가입에는 CI 저장이 가능한 인증수단만 사용 가능합니다.
            </p>
          )}
        </div>

        {/* 인증하기 버튼 */}
        <div className={styles.footer}>
          <button
            className={styles.authButton}
            onClick={handleAuth}
            disabled={!isAuthButtonEnabled()}
          >
            {!isSdkReady ? '로딩 중...' : isLoading ? '인증 중...' : '인증하기'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default UnifiedAuthModal;
